import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import Reveal from "@/components/site/Reveal";

function CountUp({ to, suffix = "", testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <p ref={ref} className="font-display text-4xl sm:text-5xl lg:text-6xl gold-text" data-testid={testId}>
      {val}
      {suffix}
    </p>
  );
}

const IMG_DETAIL = "https://images.pexels.com/photos/34803747/pexels-photo-34803747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_INTERIOR = "/images/about-dining.jpg";
const IMG_HOME = "/images/about-living.jpg";

const stats = [
  { value: 22, suffix: "+", label: "Years of Experience" },
  { value: 15, suffix: "+", label: "Projects Completed" },
  { value: 250, suffix: "+", label: "Units Sold" },
];

const values = ["Integrity", "Quality", "Innovation", "Customer Trust"];

function EditorialImage({ src, alt, testId, tall = false }) {
  return (
    <div className="overflow-hidden border border-[#C5A059]/20 p-3 spotlight-frame group" data-testid={testId}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full ${tall ? "aspect-[3/4]" : "aspect-[4/3]"} object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out`}
      />
    </div>
  );
}

export default function About() {
  return (
    <section id="about" className="border-y border-white/10 bg-[#0A0A0A]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <Reveal>
          <p className="font-accent text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">02 — About Us</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight max-w-3xl" data-testid="about-title">
            Building Trust. <span className="gold-text italic">Creating Landmarks.</span> Shaping the Future.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-3 gap-6 border-y border-[#C5A059]/20 py-10" data-testid="about-stats">
            {stats.map((s) => (
              <div key={s.label}>
                <CountUp to={s.value} suffix={s.suffix} testId={`about-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} />
                <p className="mt-2 font-accent text-[10px] sm:text-xs tracking-[0.2em] uppercase text-zinc-500">{s.label}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <p className="text-zinc-300 text-base leading-relaxed" data-testid="about-para-1">
              With over <span className="text-[#C5A059]">22+ years of experience</span> in the real estate and construction industry, Taha Developers has established itself as a trusted name in quality construction and thoughtfully designed spaces.
            </p>
            <p className="mt-6 text-zinc-400 text-base leading-relaxed">
              Over the years, we have successfully completed <span className="text-[#C5A059]">15+ residential and commercial projects</span>, delivering homes and spaces that combine quality, functionality, modern design, and lasting value.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <EditorialImage src={IMG_DETAIL} alt="Architectural craftsmanship by Taha Developers" testId="about-image-1" />
          </Reveal>
        </div>

        <div className="mt-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:order-1 order-2">
            <EditorialImage src={IMG_INTERIOR} alt="Elegant interiors crafted by Taha Developers" testId="about-image-2" tall />
          </Reveal>
          <Reveal delay={0.15} className="lg:order-2 order-1">
            <p className="font-display text-2xl sm:text-3xl tracking-tight leading-snug" data-testid="about-quote">
              "Every project is more than just a structure — it is a <span className="gold-text italic">commitment to quality, trust, and customer satisfaction</span>."
            </p>
            <p className="mt-6 text-zinc-400 text-base leading-relaxed" data-testid="about-para-2">
              From selecting the right location and materials to thoughtful planning and execution, we focus on every detail to create spaces that people are proud to own.
            </p>
          </Reveal>
        </div>

        <div className="mt-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <p className="text-zinc-300 text-base leading-relaxed" data-testid="about-para-3">
              Our portfolio includes <span className="text-[#C5A059]">residential developments, commercial spaces, and mixed-use projects</span>, designed to meet the evolving needs of modern families, businesses, and communities.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <EditorialImage src={IMG_HOME} alt="Luxury living spaces by Taha Developers" testId="about-image-3" />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 border border-[#C5A059]/30 bg-black p-10 lg:p-14" data-testid="about-commitment">
            <p className="font-accent text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-6">Our Commitment</p>
            <p className="text-zinc-300 text-base leading-relaxed max-w-3xl" data-testid="about-commitment-text">
              With a strong foundation built over two decades, we continue to move forward with the same values that have shaped our journey. As we look towards the future, Taha Developers remains committed to creating exceptional spaces, delivering superior construction, and building relationships that last for generations.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {values.map((v, i) => (
                <span key={v} className="flex items-center gap-3 font-accent text-xs tracking-[0.2em] uppercase text-zinc-300 border border-white/15 px-5 py-3 hover:border-[#C5A059]/60 hover:text-[#C5A059] transition-colors duration-300" data-testid={`about-value-${i}`}>
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#C5A059]" /> {v}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
